import React, { useCallback, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from 'src/components/Table/TableContainer';
import { columnsOrderDetails, detailsHeadings } from './constants';
import TableHeading from 'src/components/Table/TableHeading';
import { Box, TableBody, CircularProgress } from '@mui/material';
import NoDataCell from 'src/components/NoDataCell';
import { pagesText } from 'src/constants/pagesText';
import Row from 'src/components/Table/Row';
import LoadingRows from 'src/components/Table/LoadingRows';
import { useParams } from 'react-router-dom';
import useQueryParams from 'src/hooks/useQueryParams';
import useDebounceHandler from 'src/hooks/useDebounceHandler';
import {
  defaultSearchParams,
  reversedSortMap,
  ROW_SOURCES,
  sortingMethods,
} from 'src/constants/table';
import TableSearch from 'src/components/elements/TableSearch';
import useGetManagedInstallersAffectedDevices from '../../../../../../queries/managedInstallersDetails/useGetManagedInstallersAffectedDevices';
import useTableSort from '../../../../../../hooks/useTableSort';
import { defaultManagedInstallersDevicesFilters } from '../../../../../../components/Filters/constants';
import ManagedInstallerService from '../../../../../../services/managed-installer.service';
import useSubscribeStorage from '../../../../../../hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import Button from 'src/components/Button';
import Iconify from 'src/components/Iconify';
import { parseQueryToApiQuery } from 'src/utils/query.utils';

const ManagedInstallersDevices = () => {
  const { managedInstallerId } = useParams();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const { isLoading, data, lastElementRef, isFetching } = useGetManagedInstallersAffectedDevices({
    managedInstallerId,
  });
  const {
    queryObject: { filters = {}, searchValue: initialSearchValue },
    onApplySearchParams,
    changeFieldInURL,
  } = useQueryParams();

  const [searchValue, setSearchValue] = useState(initialSearchValue);

  const query = useMemo(
    () =>
      parseQueryToApiQuery({
        filters,
        searchValue: initialSearchValue,
      }),
    [filters, initialSearchValue],
  );

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await ManagedInstallerService.getExportDevices({
        customerEnvId,
        managedInstallerId,
        query,
        exportAffected: true,
      });

      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `affected-devices-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const { onSort, onSortTable, onResetSortField, getListItemSortMethod, getCellSortMethod } =
    useTableSort({
      filters,
      onApplySearchParams,
      searchValue,
      defaultFilters: defaultManagedInstallersDevicesFilters,
    });

  const debounceQueryHandler = useDebounceHandler((key, value) => {
    changeFieldInURL([{ key, value }]);
  }, 500);

  const onChangeSearchValue = useCallback(
    event => {
      setSearchValue(event.target.value);
      debounceQueryHandler('search', event.target.value.trim());
    },
    [debounceQueryHandler],
  );

  const onCellSort = useCallback(
    ({ field, sortMethod, sortingCellField, sortFieldName }) => {
      const reversedSortOrder =
        sortMethod === sortingMethods.initial ? sortingMethods.desc : reversedSortMap[sortMethod];

      onSort(field, reversedSortOrder, sortingCellField, sortFieldName);
    },
    [onSort],
  );

  const onResetSearch = useCallback(() => {
    setSearchValue('');
    changeFieldInURL([defaultSearchParams]);
  }, [setSearchValue, changeFieldInURL]);

  return (
    <Box sx={{ position: 'relative' }} mt={4}>
      <Button
        size="small"
        sx={{
          position: 'absolute',
          top: '-28px',
          right: '0',
          padding: '1px 8px',
        }}
        endIcon={
          isExporting ? (
            <CircularProgress size={20} sx={{ p: '2px' }} />
          ) : (
            <Iconify icon="material-symbols:download" width={20} height={20} />
          )
        }
        disabled={isExporting}
        onClick={handleExport}>
        {isExporting ? 'Exporting...' : 'Export'}
      </Button>
      <TableSearch
        searchValue={searchValue}
        onChangeSearchValue={onChangeSearchValue}
        onResetSearch={onResetSearch}
      />
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        <TableContainer
          square
          component={Paper}
          elevation={0}
          styles={{ maxHeight: `calc(100vh - 368px)` }}>
          <Table stickyHeader>
            <TableHeading
              headings={detailsHeadings}
              onSort={onSort}
              filterTopPisition="0"
              onSortTable={onSortTable}
              onResetSortField={onResetSortField}
              getListItemSortMethod={getListItemSortMethod}
              getCellSortMethod={getCellSortMethod}
              onCellSort={onCellSort}
              listCellStyles={{
                top: '0',
              }}
            />
            <TableBody>
              {!isLoading && !isFetching && !data?.length && (
                <NoDataCell colSpan={6}>{pagesText.noDataFound}</NoDataCell>
              )}
              {!!data?.length &&
                data.map((row, index) => (
                  <Row
                    withTwoColors={true}
                    ref={index === data.length - 1 ? lastElementRef : null}
                    key={index}
                    row={row}
                    columnData={columnsOrderDetails}
                    hasActions={false}
                    highlightText={true}
                    source={ROW_SOURCES.devices}
                    isClickable={true}
                  />
                ))}
            </TableBody>
            {isLoading || isFetching ? <LoadingRows columnCount={2} rowCount={3} /> : null}
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ManagedInstallersDevices;
